import React, { Component } from "react";
import { connect } from "react-redux";
import { closeModal } from "../../domain/ui/uiActions";
import Icon from "./Icon";
import * as Constants from "../../constants/Constants";
function showModal(props) {
    return props.isActiveModal && props.modalId === props.id;
}
function getFocusChildren(element) {
    const children = [];
    for (const child of element.childNodes) {
        if (child.nodeType !== Element.ELEMENT_NODE) {
            continue;
        }
        if (child.tabIndex >= 0) {
            children.push(child);
        }
        children.push(...getFocusChildren(child));
    }
    return children;
}
class Modal extends Component {
    constructor(props) {
        super(props);
        this._unhandleFocus = null;
        this._innerContent = null;
        this._closeButton = null;
        this._content = null;
        /**
         * Focus timeout
         *
         * @property _timeoutHandler
         * @type {Number}
         * @default null
         * @private
         */
        this._timeoutHandler = null;
        this._onKeyModal = this._onKeyModal.bind(this);
        this._onClickCloseBtn = this._onClickCloseBtn.bind(this);
        this._onKeyFirstBtn = this._onKeyFirstBtn.bind(this);
    }
    /* --- React Lifecycle Methods --- */
    /**
     * React lifecycle method
     *
     * @method render
     * @private
     * @returns {HTMLElement} Component element
     */
    render() {
        /* Render the modal if the modal status is active and the
        id of the modal matches the id of the modal trigger */
        if (showModal(this.props)) {
            return (<div className={`modal ${this.props.className || ""}`} onKeyDown={this._onKeyModal}>
					<div className="modal-bg" onClick={this._onClickCloseBtn}/>
					<div ref={it => this._content = it} className="modal-content" role="dialog" aria-label={this.props.label} aria-hidden="false" aria-modal="true" tabIndex={-1}>
						<button ref={it => this._closeButton = it} type="button" className="modal-content-close js-modal-btnCloseFirst" aria-label="Close Modal" onClick={this._onClickCloseBtn} onKeyDown={this._onKeyFirstBtn}>
							<span className="u-isVisuallyHidden">Close Modal</span>
							<Icon icon="close"/>
						</button>
						<div ref={it => this._innerContent = it} className="modal-content-inner">
							{this.props.children}
						</div>
					</div>
				</div>);
        }
        else {
            return null;
        }
    }
    /**
     * React lifecycle method. Called once after the component is added to the DOM.
     *
     * @method componentDidMount
     * @private
     */
    componentDidMount() {
        this._updateModalFocus(true);
    }
    componentDidUpdate(prevProps) {
        if (showModal(this.props) !== showModal(prevProps)) {
            this._updateModalFocus(false);
        }
    }
    componentWillUnmount() {
        this._tryUnhandleFocus();
    }
    /* ----- Event Methods ----- */
    /**
     * Handles when the modal close button is clicked
     *
     * @method _onClickCloseBtn
     * @private
     */
    _onClickCloseBtn() {
        this._closeModal();
    }
    /**
     * Handles key events on modal
     *
     * @method _onKeyModal
     * @param {Event} event Keydown event
     * @private
     */
    _onKeyModal(event) {
        // If key is escape key, close the modal
        if (event.keyCode === Constants.KEY.ESC) {
            event.preventDefault();
            this._closeModal();
        }
    }
    /**
     * Handles key events on first close button in modal
     *
     * @method _onKeyFirstBtn
     * @param {Event} event Keydown event
     * @private
     */
    _onKeyFirstBtn(event) {
        if (event.shiftKey && event.keyCode === Constants.KEY.TAB) {
            event.preventDefault();
            const children = getFocusChildren(this._innerContent);
            if (children.length > 0) {
                const last = children[children.length - 1];
                last.focus();
            }
        }
    }
    _updateModalFocus(initialMount) {
        clearTimeout(this._timeoutHandler);
        if (showModal(this.props)) {
            /* If the modal is open, focus on the close button.
            Using timeout in case focusing for routing happens first */
            this._timeoutHandler = setTimeout(() => {
                if (this._closeButton != null) {
                    this._closeButton.focus();
                    this._closeButton.setAttribute("data-test", "ready");
                }
            }, 500);
            const handleFocus = (event) => {
                if (this._closeButton !== event.target
                    && this._content !== event.target
                    && !this._innerContent.contains(event.target)) {
                    // we've tabbed out of the modal, set focus back to start
                    this._closeButton.focus();
                }
            };
            document.addEventListener("focusin", handleFocus);
            this._unhandleFocus = () => document.removeEventListener("focusin", handleFocus);
        }
        else {
            this._tryUnhandleFocus();
            if (!initialMount && !this.props.isActiveModal && this.props.triggerId.length > 0) {
                /* If modal has closed, set focus on the trigger.
                Using timeout in case focusing for routing happens first */
                this._timeoutHandler = setTimeout(() => {
                    const trigger = document.querySelector(`.js-modal-btnTrigger[data-modaltriggerid='${this.props.triggerId}']`);
                    if (trigger != null) {
                        trigger.focus();
                    }
                }, 500);
            }
        }
    }
    _tryUnhandleFocus() {
        if (this._unhandleFocus) {
            this._unhandleFocus();
            this._unhandleFocus = null;
        }
    }
    /**
     * Close modal, fire callback if present
     *
     * @method _closeModal
     * @private
     */
    _closeModal() {
        if (this.props.onCloseCallback) {
            this.props.onCloseCallback();
        }
        this.props.closeModal({
            modalId: this.props.modalId,
            triggerId: this.props.triggerId
        });
    }
}
Modal.defaultProps = {
    label: ""
};
export { Modal };
/* --- React Redux bootstrapping --- */
const mapStateToProps = (state) => {
    return {
        isActiveModal: state.ui.isActiveModal,
        modalId: state.ui.modalId,
        triggerId: state.ui.triggerId
    };
};
const bindActionsToProps = {
    closeModal
};
/* Create and export container component that wraps Modal for property binding */
export default connect(mapStateToProps, bindActionsToProps)(Modal);



// WEBPACK FOOTER //
// ./src/assets/scripts/components/presentational/Modal.tsx